import React from 'react';
import { Typography } from '@/components/common';

interface Props {
	name: string;
	fullName: string;
}

export const RetainerSecurityBody = ({ name, fullName }: Props) => {
	return (
		<div className="space-y-6 text-[#0D0616] opacity-75 mb-4">
			<Typography type="regular-14" className="break-words">
				Dear {`${name}`}:
			</Typography>
			<Typography type="regular-14" className="text-justify break-words">
				This agreement is made between the law firm Lowey Dannenberg, P.C. and{' '}
				{`${fullName}`} (“You”) whereby You retain and employ Lowey Dannenberg
				to represent You in connection with an application for a lead plaintiff.
				By executing this Retainer Agreement, You agree to serve as a lead
				plaintiff, pursuant to the terms and undertakings set forth herein, if
				so appointed.
			</Typography>

			{/* SECTION I */}
			<Typography type="semibold-18" className="underline">
				I. SCOPE OF SERVICES
			</Typography>
			<Typography type="regular-14" className="text-justify">
				You understand that Lowey Dannenberg may seek court approval to appoint
				You, in conjunction with possibly others who have made similar requests,
				as lead plaintiff(s) in the action which we propose to file on Your
				behalf, or in connection with subsequently filed actions arising out of
				the same facts. If the court does not appoint You as a lead plaintiff or
				You are not included as a plaintiff in the operative complaint in the
				case, then Lowey Dannenberg&apos;s representation of You shall
				automatically terminate. Lowey Dannenberg shall promptly notify You that
				the court has appointed You as a lead plaintiff or that You are included
				as a plaintiff in the operative complaint in the case.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				We shall keep You advised of all material events in this litigation, as
				is our responsibility as attorneys. We shall provide You with copies of
				all significant pleadings, motions, or settlement demands in the action
				and may ask You to review and approve certain items before we can take
				action on Your behalf. We will have reasonable cooperation from You
				throughout the case, for example, to respond to discovery or other
				requests for information likely to be received from defendants, or
				offers to resolve Your claims.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				As a plaintiff, you will have certain other responsibilities including,
				keeping current as to how the case is progressing, responding to our
				requests for documents or information, making yourself reasonably
				available for deposition, assisting with the collection and production
				of documents, and appearing at a trial or arbitration, if necessary.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				You should preserve and stop the deletion or destruction of any
				documents, data, and devices (e.g., like your cell phone or computers)
				that may contain information related to the litigation. You should
				contact us before You trade in, sell, or dispose of any devices that may
				be relevant to this case, for examples, devices You used to access any
				defendant&apos;s web sites or apps, or that You used to communicate with
				a defendant or their representatives.
			</Typography>
			{/* END SECTION I */}

			{/* SECTION II */}
			<Typography type="semibold-18" className="underline">
				II. CONTINGENT FEE AGREEMENT
			</Typography>
			<Typography type="regular-14" className="text-justify">
				The Firm will represent You as lead plaintiff on a fully contingency fee
				basis. In addition, Lowey Dannenberg will advance all costs and expenses
				that plaintiffs&apos; counsel deem necessary to prosecute the case,
				which may include filing fees, court reporting and transcript costs,
				expert fees, research fees, document copying, reasonable travel costs,
				overnight delivery charges, and other office expenses related to the
				work performed on Your case. If the lawsuit generates a fund for the
				class, Lowey Dannenberg will seek their fees, costs, and expenses by
				application to the Court. This means: (1) that You will not be
				responsible for the payment of any legal fees or expenses to Lowey
				Dannenberg regardless of the outcome of the case, and (2) that Lowey
				Dannenberg will only receive compensation if they are successful at
				recovering money for You and/or the class, for example as part of a
				settlement, at trial, or award following an arbitration.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				In the event that Lowey Dannenberg is successful in recovering any money
				as part of a settlement, judgment, verdict, or otherwise on behalf of
				You and the class, Lowey Dannenberg may apply for a fee up to 33 1/3% of
				the total amount recovered and reimbursement of expenses, subject to
				court approval. These fees and expenses may be paid from the settlement,
				judgment, or other award, or separately by one or more of the defendants
				in the action, and will not be charged to You. In the event a defendant
				agrees to pay fees and expenses separate from any recovery paid to You
				(e.g., as part of an agreement to pay attorney&apos;s fees in an
				arbitration agreement) Lowey Dannenberg will be entitled to the maximum
				amount of attorneys&apos; fees and expenses available under that
				agreement.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				Any monies recovered from defendants on behalf of the class will be
				divided among class members pursuant to a court-ordered plan of
				allocation that considers, among other things, the relative proportion
				of total losses incurred by You and each other class member. Under the
				rules governing class action litigation, while the lead plaintiff(s)
				recover according to the same formula as other class members, the court
				may approve an application for reimbursement of the lead
				plaintiffs&apos; reasonable costs and expenses directly related to the
				representation of the class. Examples are lost wages and travel expenses
				associated with testifying in the action. We may make an application for
				reimbursement on your behalf if you qualify at the end of the case.
			</Typography>
			{/* END SECTION II */}

			{/* SECTION III */}
			<Typography type="semibold-18" className="underline">
				III. TERMINATION
			</Typography>
			<Typography type="regular-14" className="text-justify">
				You shall have the right at any time to terminate our services and
				representation upon written notice. If this occurs, You still will not
				owe Lowey Dannenberg for fees and expenses, however, Lowey Dannenberg
				will be entitled to seek reimbursement for the fees and expenses
				incurred prior to termination from any eventual settlement, verdict,
				award, or other resolution reached in the case (e.g., should You retain
				other lawyers and they reach a settlement on behalf of You or a class of
				similarly situated individuals). Unless specifically agreed by You and
				Lowey Dannenberg, Lowey Dannenberg will provide no further services and
				advance no further costs on Your behalf after receipt of the termination
				notice.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				We also reserve the right to terminate our representation of You at any
				time. This includes if You fail to respond to our communications or
				participate in the action, or if there is a disagreement about how to
				proceed with the prosecution of Your case.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				If Lowey Dannenberg is terminated for any reason, Lowey Dannenberg shall
				be entitled, upon a recovery being obtained in the litigation and from
				the proceeds of that recovery, to quantum meruit for legal services
				rendered in connection with the litigation and to the reimbursement of
				costs and expenses incurred by Lowey Dannenberg in the litigation, all
				as approved by the Court.
			</Typography>
			{/* END SECTION III */}

			{/* SECTION IV */}
			<Typography type="semibold-18" className="underline">
				IV. OTHER TERMS
			</Typography>
			<Typography type="regular-14" className="text-justify">
				In certain circumstances, You may have the right to arbitration of fee
				disputes under 22 N.Y. C.R.R. 137. If You are not entitled by law to
				request binding arbitration, then any disputes arising out of this
				Retainer Agreement shall be governed by the laws of the State of New
				York and shall be brought and maintained in the courts in the State of
				New York which shall have exclusive jurisdiction thereof.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				In order to prosecute your claims, we may associate with one or more law
				firms, for example as local counsel in a specific jurisdiction, or as
				additional plaintiffs&apos; counsel to support the work being performed
				on the case. Any such firms that agree to be named as counsel for You
				will be bound by the same terms and conditions as set forth herein.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				You consent to Lowey Dannenberg&apos;s collection, use and disclosure of
				personal information, such as a name, email address or other data You
				volunteer to provide to Lowey Dannenberg or its affiliate Claim Magic as
				described in Claim Magic Privacy Policy.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				We make no warranties or representations concerning the eventual outcome
				of any action in which you serve as a plaintiff and/or class
				representative.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				This agreement sets forth the entire Retainer Agreement between the
				parties, and supersedes all other oral or written provisions.
			</Typography>
			<Typography type="regular-14" className="text-justify">
				If You find everything in order, please sign below. We greatly
				appreciate the opportunity to be of service to You and look forward to
				working on Your behalf in obtaining favorable results.
			</Typography>
		</div>
	);
};
